import { get, set } from 'lodash-es';
import {
  DEALER_RETAIL_VALUE,
  PRICE_NAMES_USED,
  PRIVATE_PARTY_NO_OFFER_VALUE_DIFFERENCE_MESSAGES,
  PRIVATE_PARTY_VALUE,
  TRADE_IN_VALUE,
} from 'site-modules/shared/constants/appraisal/appraisal';

export const getConditionPricing = (tmvConditions = {}) => {
  const partnerConditionPricing = {};
  Object.keys(tmvConditions).forEach(condition => {
    partnerConditionPricing[condition.toLowerCase()] = get(
      tmvConditions,
      `${condition}.Current.totalWithOptions.usedTradeIn`
    );
  });
  return partnerConditionPricing;
};

const getPricesObject = priceDataObject => {
  const { usedTradeIn, usedPrivateParty, usedTmvRetail } = priceDataObject || {};
  if (!usedTradeIn && !usedPrivateParty && !usedTmvRetail) return {};
  return {
    [TRADE_IN_VALUE]: usedTradeIn,
    [PRIVATE_PARTY_VALUE]: usedPrivateParty,
    [DEALER_RETAIL_VALUE]: usedTmvRetail,
  };
};

const getOptionsTotalPrices = options =>
  PRICE_NAMES_USED.reduce((priceDataObject, name) => {
    const priceSum = options.reduce((acc, curr) => acc + get(curr, `${name}`, 0), 0);
    return set(priceDataObject, name, priceSum);
  }, {});

export const getAllTmvPrices = (condition, tmvConditions, options) => {
  const CONDITION = condition.toUpperCase();
  const nationalBasePrice = get(tmvConditions, `${CONDITION}.Current.nationalBasePrice`);
  const regionalAdjustment = get(tmvConditions, `${CONDITION}.Current.regionalAdjustment`);
  const totalWithOptions = get(tmvConditions, `${CONDITION}.Current.totalWithOptions`);
  const mileageAdjustment = get(tmvConditions, `${CONDITION}.Current.mileageAdjustment`);
  const colorAdjustment = get(tmvConditions, `${CONDITION}.Current.colorAdjustment`);
  const optionsPrices = get(tmvConditions, `${CONDITION}.Current.optionTMVPrices`, {});
  const conditionAdjustment = get(tmvConditions, `${CONDITION}.Current.conditionAdjustment`);
  const certifiedUsedPrice = get(tmvConditions, `${CONDITION}.Current.certifiedUsedPrice`);

  return {
    nationalPrices: getPricesObject(nationalBasePrice),
    regionalPrices: getPricesObject(regionalAdjustment),
    totalPrices: getPricesObject(totalWithOptions),
    mileagePrices: getPricesObject(mileageAdjustment),
    colorPrices: getPricesObject(colorAdjustment),
    optionalPrices: getPricesObject(getOptionsTotalPrices(Object.values(optionsPrices))),
    optionalPricesValues:
      options &&
      options.map(({ id, name }) => ({
        id,
        name,
        price: getPricesObject(optionsPrices[id]),
      })),
    conditionPrices: getPricesObject(conditionAdjustment),
    conditionPricing: getConditionPricing(tmvConditions),
    certifiedUsedPrice: getPricesObject(certifiedUsedPrice),
  };
};

/**
 * @returns {Object} Price breakdown by condition.
 *  E.g., returnedObject['rough']['nationalPrices'] = National prices by appraisal type etc.
 */
export const getAllTmvConditionPricing = (tmvConditions, options) => {
  const conditionPricing = {};
  Object.keys(tmvConditions).forEach(condition => {
    conditionPricing[condition.toLowerCase()] = getAllTmvPrices(condition, tmvConditions, options);
  });
  return conditionPricing;
};

export const getPrivatePartyNoOfferPricing = ({ tmvConditions, options, condition }) => {
  const allConditionPricing = getAllTmvConditionPricing(tmvConditions, options);
  const totalPrices = get(allConditionPricing, [condition?.toLowerCase(), 'totalPrices'], {});
  const privatePartyValue = get(totalPrices, PRIVATE_PARTY_VALUE);
  const privatePartyPrice = privatePartyValue || privatePartyValue === 0 ? Number(privatePartyValue) : null;
  const tradeInValue = get(totalPrices, TRADE_IN_VALUE);
  const tradeInPrice = tradeInValue || tradeInValue === 0 ? Number(tradeInValue) : null;
  const valueDifference =
    Number.isFinite(privatePartyPrice) && Number.isFinite(tradeInPrice) ? privatePartyPrice - tradeInPrice : null;

  return {
    privatePartyPrice,
    valueDifference,
    valueDifferenceMessage:
      !Number.isFinite(valueDifference) || valueDifference < 100
        ? ''
        : PRIVATE_PARTY_NO_OFFER_VALUE_DIFFERENCE_MESSAGES.find(({ max }) => valueDifference <= max)?.message || '',
  };
};

/**
 * Checks whether both nationalBasePrice and totalWithOptions have valid price data
 * @param {string} condition
 * @param {Object} tmvConditions
 * @returns {boolean}
 */
export const hasValidTmvData = (condition, tmvConditions = {}) => {
  const conditionUppercased = condition.toUpperCase();
  const nationalBasePrice = get(tmvConditions, `${conditionUppercased}.Current.nationalBasePrice`, {});
  const totalWithOptions = get(tmvConditions, `${conditionUppercased}.Current.totalWithOptions`, {});
  const hasNationalPrices = !!(
    nationalBasePrice?.usedTradeIn ||
    nationalBasePrice?.usedPrivateParty ||
    nationalBasePrice?.usedTmvRetail
  );
  const hasTotalPrices = !!(
    totalWithOptions?.usedTradeIn ||
    totalWithOptions?.usedPrivateParty ||
    totalWithOptions?.usedTmvRetail
  );
  return hasNationalPrices && hasTotalPrices;
};

/**
 * Gets Total price breakdown by condition.
 * @param {Object} allConditionPricing Price breakdown by condition.
 * @returns {Object} Total Price breakdown by condition.
 */
export const getAllConditionTotalPrices = (allConditionPricing = {}) => {
  const conditionPrices = {};
  Object.keys(allConditionPricing).forEach(condition => {
    conditionPrices[condition] = allConditionPricing[condition] && allConditionPricing[condition].totalPrices;
  });
  return conditionPrices;
};
